import React, { useState } from 'react';
import {
  useCreateBridgeCardMutation,
  useListBridgeCardsQuery,
  useGetBridgeCardQuery,
  useFreezeBridgeCardMutation,
  useUnfreezeBridgeCardMutation,
  useGetBridgeCardTransactionsQuery,
  useGenerateCardDetailsTokenMutation,
} from '../../redux/api/authorized/bridgeCards';
import {
  useCheckKycStatusQuery,
  useCreateKycLinkMutation,
  useGetKycStatusQuery,
  useGetBridgeCustomerQuery,
} from '../../redux/api/authorized/bridge';
import {
  useInitiatePrepaidCardMutation,
  useBroadcastAndCreatePrepaidCardMutation,
  useListPrepaidCardsQuery,
  useGetPrepaidCardStatusQuery,
  useCreateOrderDepositMutation,
  usePrepareDepositMutation,
  useBroadcastDepositMutation,
} from '../../redux/api/authorized/zyptoPrepaidCards';
import { useSolana } from '../../hooks/useSolana';

const BridgeCardsTest = () => {
  const { signAndSerializeTransaction } = useSolana();

  const [cardId, setCardId] = useState('');
  const [currency, setCurrency] = useState('usdc');
  const [chain, setChain] = useState('solana');
  const [email, setEmail] = useState('');
  const [response, setResponse] = useState(null);
  const [error, setError] = useState(null);

  // Zypto Prepaid Card state
  const [prepaidAmount, setPrepaidAmount] = useState('');
  const [serializedTransaction, setSerializedTransaction] = useState('');
  const [prepaidCardId, setPrepaidCardId] = useState('');
  const [orderDepositAmount, setOrderDepositAmount] = useState('');
  const [orderDepositCurrency, setOrderDepositCurrency] = useState('USD');
  const [tokenSymbol, setTokenSymbol] = useState('SOL.USDC');
  const [productId, setProductId] = useState('');
  const [depositSerializedTransaction, setDepositSerializedTransaction] = useState('');

  // Mutations
  const [createCard, { isLoading: isCreating }] = useCreateBridgeCardMutation();
  const [freezeCard, { isLoading: isFreezing }] = useFreezeBridgeCardMutation();
  const [unfreezeCard, { isLoading: isUnfreezing }] = useUnfreezeBridgeCardMutation();
  const [generateToken, { isLoading: isGeneratingToken }] = useGenerateCardDetailsTokenMutation();
  const [createKycLink, { isLoading: isCreatingKycLink }] = useCreateKycLinkMutation();

  // Zypto Prepaid Card mutations
  const [initiatePrepaidCard, { isLoading: isInitiatingPrepaid }] = useInitiatePrepaidCardMutation();
  const [broadcastAndCreatePrepaidCard, { isLoading: isBroadcastingPrepaid }] = useBroadcastAndCreatePrepaidCardMutation();
  const [createOrderDeposit, { isLoading: isCreatingOrderDeposit }] = useCreateOrderDepositMutation();
  const [prepareDeposit, { isLoading: isPreparingDeposit }] = usePrepareDepositMutation();
  const [broadcastDeposit, { isLoading: isBroadcastingDeposit }] = useBroadcastDepositMutation();

  // Queries
  const { data: cardsList, refetch: refetchCards, isLoading: isLoadingCards } = useListBridgeCardsQuery();
  const { data: cardDetails, refetch: refetchCardDetails, isLoading: isLoadingCardDetails } = useGetBridgeCardQuery(cardId, {
    skip: !cardId
  });
  const { data: transactions, refetch: refetchTransactions, isLoading: isLoadingTransactions } = useGetBridgeCardTransactionsQuery(
    { cardId },
    { skip: !cardId }
  );
  const { data: kycStatus, refetch: refetchKycStatus, isLoading: isLoadingKycStatus } = useCheckKycStatusQuery();
  const { data: bridgeCustomer, refetch: refetchBridgeCustomer, isLoading: isLoadingBridgeCustomer } = useGetBridgeCustomerQuery();

  // Zypto Prepaid Card queries
  const { data: prepaidCardsList, refetch: refetchPrepaidCards, isLoading: isLoadingPrepaidCards } = useListPrepaidCardsQuery();
  const { data: prepaidCardStatus, refetch: refetchPrepaidCardStatus, isLoading: isLoadingPrepaidCardStatus } = useGetPrepaidCardStatusQuery(prepaidCardId, {
    skip: !prepaidCardId
  });

  const handleApiCall = async (apiCall, successMessage) => {
    setError(null);
    const result = await apiCall();

    if (result.error) {
      setError({
        message: 'API Error',
        error: result.error.data || result.error.message || 'Unknown error',
        timestamp: new Date().toISOString()
      });
    } else {
      setResponse({
        message: successMessage,
        data: result.data,
        timestamp: new Date().toISOString()
      });
    }
  };

  const handleCreateCard = () => {
    handleApiCall(
      () => createCard({ currency, chain }),
      'Card created successfully'
    );
  };

  const handleFreezeCard = () => {
    if (!cardId) {
      setError({ message: 'Please enter a card ID' });
      return;
    }
    handleApiCall(
      () => freezeCard(cardId),
      'Card frozen successfully'
    );
  };

  const handleUnfreezeCard = () => {
    if (!cardId) {
      setError({ message: 'Please enter a card ID' });
      return;
    }
    handleApiCall(
      () => unfreezeCard(cardId),
      'Card unfrozen successfully'
    );
  };

  const handleGenerateToken = () => {
    if (!cardId) {
      setError({ message: 'Please enter a card ID' });
      return;
    }
    handleApiCall(
      () => generateToken(cardId),
      'Card details token generated successfully'
    );
  };

  const handleListCards = async () => {
    const result = await refetchCards();

    if (result.error) {
      setError({
        message: 'API Error',
        error: result.error.data || result.error.message || 'Unknown error',
        timestamp: new Date().toISOString()
      });
    } else {
      setResponse({
        message: 'Cards list refreshed',
        data: result.data,
        timestamp: new Date().toISOString()
      });
    }
  };

  const handleGetCardDetails = async () => {
    if (!cardId) {
      setError({ message: 'Please enter a card ID' });
      return;
    }

    const result = await refetchCardDetails();

    if (result.error) {
      setError({
        message: 'API Error',
        error: result.error.data || result.error.message || 'Unknown error',
        timestamp: new Date().toISOString()
      });
    } else {
      setResponse({
        message: 'Card details refreshed',
        data: result.data,
        timestamp: new Date().toISOString()
      });
    }
  };

  const handleGetTransactions = async () => {
    if (!cardId) {
      setError({ message: 'Please enter a card ID' });
      return;
    }

    const result = await refetchTransactions();

    if (result.error) {
      setError({
        message: 'API Error',
        error: result.error.data || result.error.message || 'Unknown error',
        timestamp: new Date().toISOString()
      });
    } else {
      setResponse({
        message: 'Transactions refreshed',
        data: result.data,
        timestamp: new Date().toISOString()
      });
    }
  };

  const handleCreateKycLink = () => {
    if (!email) {
      setError({ message: 'Please enter an email address' });
      return;
    }
    handleApiCall(
      () => createKycLink({ email }),
      'KYC link created successfully'
    );
  };

  const handleCheckKycStatus = async () => {
    const result = await refetchKycStatus();

    if (result.error) {
      setError({
        message: 'API Error',
        error: result.error.data || result.error.message || 'Unknown error',
        timestamp: new Date().toISOString()
      });
    } else {
      setResponse({
        message: 'KYC status refreshed',
        data: result.data,
        timestamp: new Date().toISOString()
      });
    }
  };

  const handleGetBridgeCustomer = async () => {
    const result = await refetchBridgeCustomer();

    if (result.error) {
      setError({
        message: 'API Error',
        error: result.error.data || result.error.message || 'Unknown error',
        timestamp: new Date().toISOString()
      });
    } else {
      setResponse({
        message: 'Bridge customer data refreshed',
        data: result.data,
        timestamp: new Date().toISOString()
      });
    }
  };

  // Zypto Prepaid Card handlers
  const handleInitiatePrepaidCard = () => {
    if (!prepaidAmount) {
      setError({ message: 'Please enter an amount' });
      return;
    }
    handleApiCall(
      () => initiatePrepaidCard({ amount: parseFloat(prepaidAmount) }),
      'Prepaid card initiated successfully'
    );
  };

  const handleBroadcastAndCreatePrepaidCard = async () => {
    if (!serializedTransaction) {
      setError({ message: 'Please enter serialized transaction' });
      return;
    }
    if (!prepaidCardId) {
      setError({ message: 'Please enter prepaid card ID' });
      return;
    }

    try {
      setError(null);
      // Sign the transaction first
      const signedTransaction = await signAndSerializeTransaction(serializedTransaction);

      // Then broadcast it
      const result = await broadcastAndCreatePrepaidCard({
        serializedTransaction: signedTransaction,
        cardId: prepaidCardId
      });

      if (result.error) {
        setError({
          message: 'API Error',
          error: result.error.data || result.error.message || 'Unknown error',
          timestamp: new Date().toISOString()
        });
      } else {
        setResponse({
          message: 'Prepaid card broadcasted and created successfully',
          data: result.data,
          timestamp: new Date().toISOString()
        });
      }
    } catch (err) {
      setError({
        message: 'Signing Error',
        error: err.message || 'Failed to sign transaction',
        timestamp: new Date().toISOString()
      });
    }
  };

  const handleListPrepaidCards = async () => {
    const result = await refetchPrepaidCards();

    if (result.error) {
      setError({
        message: 'API Error',
        error: result.error.data || result.error.message || 'Unknown error',
        timestamp: new Date().toISOString()
      });
    } else {
      setResponse({
        message: 'Prepaid cards list refreshed',
        data: result.data,
        timestamp: new Date().toISOString()
      });
    }
  };

  const handleGetPrepaidCardStatus = async () => {
    if (!prepaidCardId) {
      setError({ message: 'Please enter a prepaid card ID' });
      return;
    }

    const result = await refetchPrepaidCardStatus();

    if (result.error) {
      setError({
        message: 'API Error',
        error: result.error.data || result.error.message || 'Unknown error',
        timestamp: new Date().toISOString()
      });
    } else {
      setResponse({
        message: 'Prepaid card status refreshed',
        data: result.data,
        timestamp: new Date().toISOString()
      });
    }
  };

  const handleCreateOrderDeposit = () => {
    if (!orderDepositAmount) {
      setError({ message: 'Please enter an amount' });
      return;
    }
    handleApiCall(
      () => createOrderDeposit({
        amount: parseFloat(orderDepositAmount),
        currency: orderDepositCurrency,
        tokenSymbol,
        productId: productId || undefined
      }),
      'Order deposit created successfully'
    );
  };

  const handlePrepareDeposit = () => {
    if (!prepaidCardId) {
      setError({ message: 'Please enter a prepaid card ID' });
      return;
    }
    handleApiCall(
      () => prepareDeposit(prepaidCardId),
      'Deposit transaction prepared successfully'
    );
  };

  const handleBroadcastDeposit = async () => {
    if (!depositSerializedTransaction) {
      setError({ message: 'Please enter deposit serialized transaction' });
      return;
    }
    if (!prepaidCardId) {
      setError({ message: 'Please enter prepaid card ID' });
      return;
    }

    try {
      setError(null);
      // Sign the transaction first
      const signedTransaction = await signAndSerializeTransaction(depositSerializedTransaction);

      // Then broadcast it
      const result = await broadcastDeposit({
        cardId: prepaidCardId,
        serializedTransaction: signedTransaction
      });

      if (result.error) {
        setError({
          message: 'API Error',
          error: result.error.data || result.error.message || 'Unknown error',
          timestamp: new Date().toISOString()
        });
      } else {
        setResponse({
          message: 'Deposit transaction broadcast successfully',
          data: result.data,
          timestamp: new Date().toISOString()
        });
      }
    } catch (err) {
      setError({
        message: 'Signing Error',
        error: err.message || 'Failed to sign transaction',
        timestamp: new Date().toISOString()
      });
    }
  };

  return (
    <div style={{ padding: '20px', maxWidth: '1200px', margin: '0 auto' }}>
      <h1>Bridge Cards & Zypto Prepaid Cards API Test</h1>

      {/* ========== BRIDGE CARDS SECTION ========== */}
      <div style={{ marginBottom: '40px', padding: '20px', border: '2px solid #007bff', borderRadius: '8px', backgroundColor: '#f0f8ff' }}>
        <h2 style={{ marginTop: 0, color: '#007bff' }}>Bridge Cards</h2>

        {/* Bridge Input Controls */}
        <div style={{ marginBottom: '20px', padding: '20px', border: '1px solid #ccc', borderRadius: '8px', backgroundColor: 'white' }}>
          <h3>Bridge Card Controls</h3>

        <div style={{ marginBottom: '15px' }}>
          <label style={{ display: 'block', marginBottom: '5px' }}>Card ID:</label>
          <input
            type="text"
            value={cardId}
            onChange={(e) => setCardId(e.target.value)}
            placeholder="Enter card ID for specific operations"
            style={{ width: '300px', padding: '8px', marginRight: '10px' }}
          />
        </div>

        <div style={{ marginBottom: '15px' }}>
          <label style={{ display: 'block', marginBottom: '5px' }}>Currency:</label>
          <select
            value={currency}
            onChange={(e) => setCurrency(e.target.value)}
            style={{ padding: '8px', marginRight: '10px' }}
          >
            <option value="usdc">USDC</option>
            <option value="usd">USD</option>
          </select>
        </div>

        <div style={{ marginBottom: '15px' }}>
          <label style={{ display: 'block', marginBottom: '5px' }}>Chain:</label>
          <select
            value={chain}
            onChange={(e) => setChain(e.target.value)}
            style={{ padding: '8px', marginRight: '10px' }}
          >
            <option value="solana">Solana</option>
            <option value="ethereum">Ethereum</option>
          </select>
        </div>

        <div style={{ marginBottom: '15px' }}>
          <label style={{ display: 'block', marginBottom: '5px' }}>Email (for KYC):</label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Enter email for KYC link creation"
            style={{ width: '300px', padding: '8px', marginRight: '10px' }}
          />
        </div>
        </div>

        {/* Bridge API Test Buttons */}
        <div style={{ marginBottom: '20px' }}>
          <h3>Bridge API Endpoints</h3>

          <h4 style={{ marginBottom: '10px', color: '#666' }}>KYC & Customer Management</h4>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '10px', marginBottom: '20px' }}>
          <button
            onClick={handleCreateKycLink}
            disabled={isCreatingKycLink || !email}
            style={{
              padding: '10px',
              backgroundColor: '#20c997',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: (isCreatingKycLink || !email) ? 'not-allowed' : 'pointer',
              opacity: (isCreatingKycLink || !email) ? 0.6 : 1
            }}
          >
            {isCreatingKycLink ? 'Creating...' : 'Create KYC Link'}
          </button>

          <button
            onClick={handleCheckKycStatus}
            disabled={isLoadingKycStatus}
            style={{
              padding: '10px',
              backgroundColor: '#6c757d',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: isLoadingKycStatus ? 'not-allowed' : 'pointer',
              opacity: isLoadingKycStatus ? 0.6 : 1
            }}
          >
            {isLoadingKycStatus ? 'Loading...' : 'Check KYC Status'}
          </button>

          <button
            onClick={handleGetBridgeCustomer}
            disabled={isLoadingBridgeCustomer}
            style={{
              padding: '10px',
              backgroundColor: '#495057',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: isLoadingBridgeCustomer ? 'not-allowed' : 'pointer',
              opacity: isLoadingBridgeCustomer ? 0.6 : 1
            }}
          >
            {isLoadingBridgeCustomer ? 'Loading...' : 'Get Bridge Customer'}
          </button>
        </div>

        <h4 style={{ marginBottom: '10px', color: '#666' }}>Card Management</h4>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '10px' }}>
          <button
            onClick={handleCreateCard}
            disabled={isCreating}
            style={{
              padding: '10px',
              backgroundColor: '#007bff',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: isCreating ? 'not-allowed' : 'pointer',
              opacity: isCreating ? 0.6 : 1
            }}
          >
            {isCreating ? 'Creating...' : 'Create Card'}
          </button>

          <button
            onClick={handleListCards}
            disabled={isLoadingCards}
            style={{
              padding: '10px',
              backgroundColor: '#28a745',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: isLoadingCards ? 'not-allowed' : 'pointer',
              opacity: isLoadingCards ? 0.6 : 1
            }}
          >
            {isLoadingCards ? 'Loading...' : 'List Cards'}
          </button>

          <button
            onClick={handleGetCardDetails}
            disabled={isLoadingCardDetails || !cardId}
            style={{
              padding: '10px',
              backgroundColor: '#17a2b8',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: (isLoadingCardDetails || !cardId) ? 'not-allowed' : 'pointer',
              opacity: (isLoadingCardDetails || !cardId) ? 0.6 : 1
            }}
          >
            {isLoadingCardDetails ? 'Loading...' : 'Get Card Details'}
          </button>

          <button
            onClick={handleFreezeCard}
            disabled={isFreezing || !cardId}
            style={{
              padding: '10px',
              backgroundColor: '#ffc107',
              color: 'black',
              border: 'none',
              borderRadius: '4px',
              cursor: (isFreezing || !cardId) ? 'not-allowed' : 'pointer',
              opacity: (isFreezing || !cardId) ? 0.6 : 1
            }}
          >
            {isFreezing ? 'Freezing...' : 'Freeze Card'}
          </button>

          <button
            onClick={handleUnfreezeCard}
            disabled={isUnfreezing || !cardId}
            style={{
              padding: '10px',
              backgroundColor: '#6f42c1',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: (isUnfreezing || !cardId) ? 'not-allowed' : 'pointer',
              opacity: (isUnfreezing || !cardId) ? 0.6 : 1
            }}
          >
            {isUnfreezing ? 'Unfreezing...' : 'Unfreeze Card'}
          </button>

          <button
            onClick={handleGetTransactions}
            disabled={isLoadingTransactions || !cardId}
            style={{
              padding: '10px',
              backgroundColor: '#fd7e14',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: (isLoadingTransactions || !cardId) ? 'not-allowed' : 'pointer',
              opacity: (isLoadingTransactions || !cardId) ? 0.6 : 1
            }}
          >
            {isLoadingTransactions ? 'Loading...' : 'Get Transactions'}
          </button>

          <button
            onClick={handleGenerateToken}
            disabled={isGeneratingToken || !cardId}
            style={{
              padding: '10px',
              backgroundColor: '#dc3545',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: (isGeneratingToken || !cardId) ? 'not-allowed' : 'pointer',
              opacity: (isGeneratingToken || !cardId) ? 0.6 : 1
            }}
          >
            {isGeneratingToken ? 'Generating...' : 'Generate Token'}
          </button>
        </div>
        </div>

        {/* Bridge Response Display */}
        <div style={{ marginBottom: '20px' }}>
          <h3>Bridge Response</h3>

          {response && (
            <div style={{
              padding: '15px',
              backgroundColor: '#d4edda',
              border: '1px solid #c3e6cb',
              borderRadius: '4px',
              marginBottom: '10px'
            }}>
              <h4 style={{ color: '#155724', margin: '0 0 10px 0' }}>Success</h4>
              <p style={{ margin: '0 0 10px 0', fontWeight: 'bold' }}>{response.message}</p>
              <p style={{ margin: '0 0 10px 0', fontSize: '12px', color: '#666' }}>
                Timestamp: {response.timestamp}
              </p>
              <pre style={{
                backgroundColor: '#f8f9fa',
                padding: '10px',
                borderRadius: '4px',
                overflow: 'auto',
                fontSize: '12px'
              }}>
                {JSON.stringify(response.data, null, 2)}
              </pre>
            </div>
          )}

          {error && (
            <div style={{
              padding: '15px',
              backgroundColor: '#f8d7da',
              border: '1px solid #f5c6cb',
              borderRadius: '4px',
              marginBottom: '10px'
            }}>
              <h4 style={{ color: '#721c24', margin: '0 0 10px 0' }}>Error</h4>
              <p style={{ margin: '0 0 10px 0', fontWeight: 'bold' }}>{error.message}</p>
              <p style={{ margin: '0 0 10px 0', fontSize: '12px', color: '#666' }}>
                Timestamp: {error.timestamp}
              </p>
              <pre style={{
                backgroundColor: '#f8f9fa',
                padding: '10px',
                borderRadius: '4px',
                overflow: 'auto',
                fontSize: '12px'
              }}>
                {JSON.stringify(error.error, null, 2)}
              </pre>
            </div>
          )}

          {!response && !error && (
            <div style={{
              padding: '15px',
              backgroundColor: '#e2e3e5',
              border: '1px solid #d6d8db',
              borderRadius: '4px',
              textAlign: 'center',
              color: '#6c757d'
            }}>
              No API calls made yet. Click a button above to test the endpoints.
            </div>
          )}
        </div>

        {/* Bridge Current Data Display */}
        <div>
          <h3>Bridge Current Data</h3>

          <h4 style={{ marginBottom: '10px', color: '#666' }}>KYC & Customer Data</h4>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
            <div>
              <h4>KYC Status</h4>
              <pre style={{
                backgroundColor: '#f8f9fa',
                padding: '10px',
                borderRadius: '4px',
                overflow: 'auto',
                fontSize: '12px',
                minHeight: '100px'
              }}>
                {kycStatus ? JSON.stringify(kycStatus, null, 2) : 'No KYC status data'}
              </pre>
            </div>

            <div>
              <h4>Bridge Customer</h4>
              <pre style={{
                backgroundColor: '#f8f9fa',
                padding: '10px',
                borderRadius: '4px',
                overflow: 'auto',
                fontSize: '12px',
                minHeight: '100px'
              }}>
                {bridgeCustomer ? JSON.stringify(bridgeCustomer, null, 2) : 'No bridge customer data'}
              </pre>
            </div>
          </div>

          <h4 style={{ marginBottom: '10px', color: '#666' }}>Card Data</h4>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
            <div>
              <h4>Cards List</h4>
              <pre style={{
                backgroundColor: '#f8f9fa',
                padding: '10px',
                borderRadius: '4px',
                overflow: 'auto',
                fontSize: '12px',
                minHeight: '100px'
              }}>
                {cardsList ? JSON.stringify(cardsList, null, 2) : 'No cards data'}
              </pre>
            </div>

            <div>
              <h4>Card Details</h4>
              <pre style={{
                backgroundColor: '#f8f9fa',
                padding: '10px',
                borderRadius: '4px',
                overflow: 'auto',
                fontSize: '12px',
                minHeight: '100px'
              }}>
                {cardDetails ? JSON.stringify(cardDetails, null, 2) : 'No card details (enter card ID)'}
              </pre>
            </div>
          </div>

          <div style={{ marginTop: '20px' }}>
            <h4>Transactions</h4>
            <pre style={{
              backgroundColor: '#f8f9fa',
              padding: '10px',
              borderRadius: '4px',
              overflow: 'auto',
              fontSize: '12px',
              minHeight: '100px'
            }}>
              {transactions ? JSON.stringify(transactions, null, 2) : 'No transactions data (enter card ID)'}
            </pre>
          </div>
        </div>
      </div>

      {/* ========== ZYPTO PREPAID CARDS SECTION ========== */}
      <div style={{ marginBottom: '20px', padding: '20px', border: '2px solid #6f42c1', borderRadius: '8px', backgroundColor: '#f8f4ff' }}>
        <h2 style={{ marginTop: 0, color: '#6f42c1' }}>Zypto Prepaid Cards</h2>

        {/* Zypto Input Controls */}
        <div style={{ marginBottom: '20px', padding: '20px', border: '1px solid #ccc', borderRadius: '8px', backgroundColor: 'white' }}>
          <h3>Zypto Prepaid Card Controls</h3>

          <div style={{ padding: '10px', backgroundColor: '#fff3cd', border: '1px solid #ffc107', borderRadius: '4px', marginBottom: '15px' }}>
            <strong>💡 Workflow:</strong>
            <ol style={{ marginBottom: 0, paddingLeft: '20px' }}>
              <li><strong>Initiate:</strong> Enter amount → Click "Initiate" → Copy cardId and serializedTransaction from response</li>
              <li><strong>Broadcast:</strong> Paste values → Click "Broadcast & Create" (transaction will be auto-signed)</li>
              <li><strong>For Order Deposits:</strong> Use "Create Order Deposit" → "Prepare Deposit" → "Broadcast Deposit"</li>
            </ol>
          </div>

        <div style={{ marginBottom: '15px' }}>
          <label style={{ display: 'block', marginBottom: '5px' }}>Prepaid Amount:</label>
          <input
            type="number"
            value={prepaidAmount}
            onChange={(e) => setPrepaidAmount(e.target.value)}
            placeholder="Enter amount (min 20)"
            style={{ width: '200px', padding: '8px', marginRight: '10px' }}
          />
        </div>

        <div style={{ marginBottom: '15px' }}>
          <label style={{ display: 'block', marginBottom: '5px' }}>Serialized Transaction:</label>
          <textarea
            value={serializedTransaction}
            onChange={(e) => setSerializedTransaction(e.target.value)}
            placeholder="Enter serialized transaction"
            style={{ width: '400px', padding: '8px', marginRight: '10px', minHeight: '60px' }}
          />
        </div>

        <div style={{ marginBottom: '15px' }}>
          <label style={{ display: 'block', marginBottom: '5px' }}>Prepaid Card ID:</label>
          <input
            type="text"
            value={prepaidCardId}
            onChange={(e) => setPrepaidCardId(e.target.value)}
            placeholder="Enter prepaid card ID"
            style={{ width: '200px', padding: '8px', marginRight: '10px' }}
          />
        </div>

        <div style={{ marginBottom: '15px' }}>
          <label style={{ display: 'block', marginBottom: '5px' }}>Order Deposit Amount:</label>
          <input
            type="number"
            value={orderDepositAmount}
            onChange={(e) => setOrderDepositAmount(e.target.value)}
            placeholder="Enter amount"
            style={{ width: '200px', padding: '8px', marginRight: '10px' }}
          />
        </div>

        <div style={{ marginBottom: '15px' }}>
          <label style={{ display: 'block', marginBottom: '5px' }}>Order Deposit Currency:</label>
          <select
            value={orderDepositCurrency}
            onChange={(e) => setOrderDepositCurrency(e.target.value)}
            style={{ padding: '8px', marginRight: '10px' }}
          >
            <option value="USD">USD</option>
            <option value="EUR">EUR</option>
            <option value="GBP">GBP</option>
          </select>
        </div>

        <div style={{ marginBottom: '15px' }}>
          <label style={{ display: 'block', marginBottom: '5px' }}>Token Symbol:</label>
          <input
            type="text"
            value={tokenSymbol}
            onChange={(e) => setTokenSymbol(e.target.value)}
            placeholder="e.g., SOL.USDC"
            style={{ width: '200px', padding: '8px', marginRight: '10px' }}
          />
        </div>

        <div style={{ marginBottom: '15px' }}>
          <label style={{ display: 'block', marginBottom: '5px' }}>Product ID (optional):</label>
          <input
            type="text"
            value={productId}
            onChange={(e) => setProductId(e.target.value)}
            placeholder="Enter product ID"
            style={{ width: '200px', padding: '8px', marginRight: '10px' }}
          />
        </div>

        <div style={{ marginBottom: '15px' }}>
          <label style={{ display: 'block', marginBottom: '5px' }}>Deposit Serialized Transaction:</label>
          <textarea
            value={depositSerializedTransaction}
            onChange={(e) => setDepositSerializedTransaction(e.target.value)}
            placeholder="Enter deposit serialized transaction"
            style={{ width: '400px', padding: '8px', marginRight: '10px', minHeight: '60px' }}
          />
        </div>
        </div>

        {/* Zypto API Test Buttons */}
        <div style={{ marginBottom: '20px' }}>
          <h3>Zypto API Endpoints</h3>

          <h4 style={{ marginBottom: '10px', color: '#666' }}>Prepaid Card Operations</h4>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '10px' }}>
          <button
            onClick={handleInitiatePrepaidCard}
            disabled={isInitiatingPrepaid || !prepaidAmount}
            style={{
              padding: '10px',
              backgroundColor: '#007bff',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: (isInitiatingPrepaid || !prepaidAmount) ? 'not-allowed' : 'pointer',
              opacity: (isInitiatingPrepaid || !prepaidAmount) ? 0.6 : 1
            }}
          >
            {isInitiatingPrepaid ? 'Initiating...' : 'Initiate Prepaid Card'}
          </button>

          <button
            onClick={handleBroadcastAndCreatePrepaidCard}
            disabled={isBroadcastingPrepaid || !serializedTransaction || !prepaidCardId}
            style={{
              padding: '10px',
              backgroundColor: '#28a745',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: (isBroadcastingPrepaid || !serializedTransaction || !prepaidCardId) ? 'not-allowed' : 'pointer',
              opacity: (isBroadcastingPrepaid || !serializedTransaction || !prepaidCardId) ? 0.6 : 1
            }}
          >
            {isBroadcastingPrepaid ? 'Broadcasting...' : 'Broadcast & Create'}
          </button>

          <button
            onClick={handleListPrepaidCards}
            disabled={isLoadingPrepaidCards}
            style={{
              padding: '10px',
              backgroundColor: '#17a2b8',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: isLoadingPrepaidCards ? 'not-allowed' : 'pointer',
              opacity: isLoadingPrepaidCards ? 0.6 : 1
            }}
          >
            {isLoadingPrepaidCards ? 'Loading...' : 'List Prepaid Cards'}
          </button>

          <button
            onClick={handleGetPrepaidCardStatus}
            disabled={isLoadingPrepaidCardStatus || !prepaidCardId}
            style={{
              padding: '10px',
              backgroundColor: '#ffc107',
              color: 'black',
              border: 'none',
              borderRadius: '4px',
              cursor: (isLoadingPrepaidCardStatus || !prepaidCardId) ? 'not-allowed' : 'pointer',
              opacity: (isLoadingPrepaidCardStatus || !prepaidCardId) ? 0.6 : 1
            }}
          >
            {isLoadingPrepaidCardStatus ? 'Loading...' : 'Get Card Status'}
          </button>

          <button
            onClick={handleCreateOrderDeposit}
            disabled={isCreatingOrderDeposit || !orderDepositAmount}
            style={{
              padding: '10px',
              backgroundColor: '#6f42c1',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: (isCreatingOrderDeposit || !orderDepositAmount) ? 'not-allowed' : 'pointer',
              opacity: (isCreatingOrderDeposit || !orderDepositAmount) ? 0.6 : 1
            }}
          >
            {isCreatingOrderDeposit ? 'Creating...' : 'Create Order Deposit'}
          </button>

          <button
            onClick={handlePrepareDeposit}
            disabled={isPreparingDeposit || !prepaidCardId}
            style={{
              padding: '10px',
              backgroundColor: '#fd7e14',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: (isPreparingDeposit || !prepaidCardId) ? 'not-allowed' : 'pointer',
              opacity: (isPreparingDeposit || !prepaidCardId) ? 0.6 : 1
            }}
          >
            {isPreparingDeposit ? 'Preparing...' : 'Prepare Deposit'}
          </button>

          <button
            onClick={handleBroadcastDeposit}
            disabled={isBroadcastingDeposit || !depositSerializedTransaction || !prepaidCardId}
            style={{
              padding: '10px',
              backgroundColor: '#dc3545',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: (isBroadcastingDeposit || !depositSerializedTransaction || !prepaidCardId) ? 'not-allowed' : 'pointer',
              opacity: (isBroadcastingDeposit || !depositSerializedTransaction || !prepaidCardId) ? 0.6 : 1
            }}
          >
            {isBroadcastingDeposit ? 'Broadcasting...' : 'Broadcast Deposit'}
          </button>
        </div>
        </div>

        {/* Zypto Response Display */}
        <div style={{ marginBottom: '20px' }}>
          <h3>Zypto Response</h3>

          {response && (
            <div style={{
              padding: '15px',
              backgroundColor: '#d4edda',
              border: '1px solid #c3e6cb',
              borderRadius: '4px',
              marginBottom: '10px'
            }}>
              <h4 style={{ color: '#155724', margin: '0 0 10px 0' }}>Success</h4>
              <p style={{ margin: '0 0 10px 0', fontWeight: 'bold' }}>{response.message}</p>
              <p style={{ margin: '0 0 10px 0', fontSize: '12px', color: '#666' }}>
                Timestamp: {response.timestamp}
              </p>
              <pre style={{
                backgroundColor: '#f8f9fa',
                padding: '10px',
                borderRadius: '4px',
                overflow: 'auto',
                fontSize: '12px'
              }}>
                {JSON.stringify(response.data, null, 2)}
              </pre>
            </div>
          )}

          {error && (
            <div style={{
              padding: '15px',
              backgroundColor: '#f8d7da',
              border: '1px solid #f5c6cb',
              borderRadius: '4px',
              marginBottom: '10px'
            }}>
              <h4 style={{ color: '#721c24', margin: '0 0 10px 0' }}>Error</h4>
              <p style={{ margin: '0 0 10px 0', fontWeight: 'bold' }}>{error.message}</p>
              <p style={{ margin: '0 0 10px 0', fontSize: '12px', color: '#666' }}>
                Timestamp: {error.timestamp}
              </p>
              <pre style={{
                backgroundColor: '#f8f9fa',
                padding: '10px',
                borderRadius: '4px',
                overflow: 'auto',
                fontSize: '12px'
              }}>
                {JSON.stringify(error.error, null, 2)}
              </pre>
            </div>
          )}

          {!response && !error && (
            <div style={{
              padding: '15px',
              backgroundColor: '#e2e3e5',
              border: '1px solid #d6d8db',
              borderRadius: '4px',
              textAlign: 'center',
              color: '#6c757d'
            }}>
              No API calls made yet. Click a button above to test the endpoints.
            </div>
          )}
        </div>

        {/* Zypto Current Data Display */}
        <div>
          <h3>Zypto Current Data</h3>

          <h4 style={{ marginBottom: '10px', color: '#666' }}>Prepaid Cards Data</h4>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
            <div>
              <h4>Prepaid Cards List</h4>
              <pre style={{
                backgroundColor: '#f8f9fa',
                padding: '10px',
                borderRadius: '4px',
                overflow: 'auto',
                fontSize: '12px',
                minHeight: '100px'
              }}>
                {prepaidCardsList ? JSON.stringify(prepaidCardsList, null, 2) : 'No prepaid cards data'}
              </pre>
            </div>

            <div>
              <h4>Prepaid Card Status</h4>
              <pre style={{
                backgroundColor: '#f8f9fa',
                padding: '10px',
                borderRadius: '4px',
                overflow: 'auto',
                fontSize: '12px',
                minHeight: '100px'
              }}>
                {prepaidCardStatus ? JSON.stringify(prepaidCardStatus, null, 2) : 'No prepaid card status (enter prepaid card ID)'}
              </pre>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BridgeCardsTest;
